import { Action } from '@reduxjs/toolkit';
import { compose } from 'ramda';
import { combineEpics, StateObservable } from 'redux-observable';
import {
  concat, of, empty, interval, Observable,
} from 'rxjs';
import {
  switchMap,
  catchError,
  tap,
  map,
  ignoreElements,
  filter,
  delay,
  delayWhen,
  first,
  pluck,
  mergeMap,
} from 'rxjs/operators';

import { EvoPlayer } from '@ezugi/bootstrap/src/views/GameVideo/EvoPlayer/player';
import { handleError } from '@ezugi/utils';

import { logError } from '../../../util/logUtils';
import VideoPlayer from '../../../views/GameVideo/NanoPlayer/VideoPlayer';
import {
  dialogActions,
  notificationActions,
  roundActions,
  socketActions,
  videoActions,
} from '../../actions';
import { NO_VIDEO_DIALOG, VIDEO_ERROR_DIALOG } from '../../constants/dialog';
import {
  volumeSelector,
  roundStatusSelector,
  gameIdSelector,
  videoReconnectSelector,
  videoStreamSoundSelector,
} from '../../selectors';
import { retailModeSelector } from '../../selectors/app';
import { notificationSelector } from '../../selectors/notification';
import { RootState } from '../../types';
import { isNoMoreBets, camelize, formatNanoStreams } from './utils';

const { socket } = socketActions;
const { video } = videoActions;
const { dialog } = dialogActions;
const { notification } = notificationActions;
const { round } = roundActions;

export const GAME_IDS_WITH_FULLWIDTH_VIDEO = [ '27', 27, '29', 29, '35', 35 ];

const FIRST_RETRIES_VIDEO_RECONNECT_MILLISECONDS_DELAY = 5000;
const LONG_TERM_RETRIES_VIDEO_RECONNECT_MILLISECONDS_DELAY = 20000;

const isFullWidth = compose(
  Array.prototype.includes.bind(GAME_IDS_WITH_FULLWIDTH_VIDEO),
  gameIdSelector
);

const videoEpic = (action$: Observable<Action>, state$: StateObservable<RootState>) => action$.pipe(
  filter(video.request.match),
  mergeMap(({ payload }) => of(
    video.set({
      cdnList: formatNanoStreams(camelize(payload)),
      fullWidth: isFullWidth(state$.value),
    }),
    video.success(),
    socket.request()
  )),
  catchError((error) => handleError({
    error,
    failAction: video.failure as any,
  }))
);

const videoFailureEpic = (action$: Observable<Action>) => action$.pipe(
  filter(video.failure.match),
  tap(() => {
    logError('video-failure', 'fatal');
  }),
  map(() => dialog.add({ name: NO_VIDEO_DIALOG }))
);

const videoErrorEpic = (action$: Observable<Action>, state$: StateObservable<RootState>) => action$.pipe(
  filter(video.error.match),
  pluck('payload'),
  filter(({ code, reason }: any) => reason !== 'normal' && code !== 1007),
  switchMap(() => {
    const roundStatus = roundStatusSelector(state$.value);
    const count = videoReconnectSelector(state$.value) as number;
    const retailMode = retailModeSelector(state$.value);

    // patch for retail games, where on video error, we should try the reconnect
    // until it will connect
    if (retailMode) {
      return concat(
        // delay values are in ms
        of(
          notification.add({
            message: 'notifications.video_reconnect',
            fallback: {
              message: 'Video has lost connection, attempting to reconnect',
            },
            autoClose: false,
            trigger: `${video.error.type}`,
          })
        ),
        of(video.reconnect()).pipe(
          delay(
            count < 5
              ? FIRST_RETRIES_VIDEO_RECONNECT_MILLISECONDS_DELAY
              : LONG_TERM_RETRIES_VIDEO_RECONNECT_MILLISECONDS_DELAY
          )
        )
      );
    }
    return !isNoMoreBets(roundStatus) && count && count < 5
      ? concat(
        of(
          notification.add({ message: 'notifications.video_unavailable' })
        ),
        of(video.reconnect()).pipe(
          delay(FIRST_RETRIES_VIDEO_RECONNECT_MILLISECONDS_DELAY)
        )
      )
      : of(
        dialog.add({
          name: isNoMoreBets(roundStatus)
            ? NO_VIDEO_DIALOG
            : VIDEO_ERROR_DIALOG,
        })
      ).pipe(
        delayWhen(() => (count
          ? action$.pipe(
            filter(round.set.match),
            filter(({ payload }) => !isNoMoreBets(payload.roundStatus!)),
            first()
          )
          : empty()))
      );
  })
);

const videoSuspendedEpic = (action$: Observable<Action>) => action$.pipe(
  filter(video.error.match),
  pluck('payload'),
  filter(({ code }: any) => code === 1007),
  switchMap(
    // eslint hack
    () => interval(1000).pipe(
      filter(() => document.hasFocus()),
      first(),
      tap(() => VideoPlayer.play()),
      catchError(({ code, message }) => of(video.failure({ code, message } as any)))
    )
  ),
  ignoreElements()
);

const videoReconnectEpic = (action$: Observable<Action>, state$: StateObservable<RootState>) => action$.pipe(
  filter(video.reconnect.match),
  tap(() => {
    const count = videoReconnectSelector(state$.value) as number;
    if (retailModeSelector(state$.value) || count <= 5) {
      VideoPlayer.play();
    }
  }),
  ignoreElements()
);

const videoPlayEpic = (action$: Observable<Action>, state$: StateObservable<RootState>) => action$.pipe(
  filter(video.play.match),
  tap(() => {
    const { value: volume, muted } = volumeSelector(state$.value);
    const videoStreamValue = videoStreamSoundSelector(state$.value);
    const isMuted = muted || !videoStreamValue;
    try {
      VideoPlayer.volume(Number(volume) / 100);
      isMuted ? VideoPlayer.mute() : VideoPlayer.unmute();
    } catch (_error) {}
  }),
  filter(() => {
    const { trigger } = notificationSelector(state$.value) || {};
    return trigger === `${video.error.type}`;
  }),
  map(() => notification.remove())
);

const videoStopEpic = (action$: Observable<Action>) => action$.pipe(
  filter(video.stop.match),
  tap(() => {
    try {
      VideoPlayer.pause();

      if (!EvoPlayer.initialized) return;
      EvoPlayer.enableVideo?.set(false);
    } catch (_err) {}
  }),
  ignoreElements()
);

const videoResumeEpic = (action$: Observable<Action>) => action$.pipe(
  filter(video.resume.match),
  tap(() => {
    try {
      VideoPlayer.play();

      if (!EvoPlayer.initialized) return;
      EvoPlayer.enableVideo?.set(true);
    } catch (_err) {}
  }),
  ignoreElements()
);

export default combineEpics(
  videoEpic,
  videoFailureEpic,
  videoErrorEpic,
  videoSuspendedEpic,
  videoReconnectEpic,
  videoPlayEpic,
  videoStopEpic,
  videoResumeEpic
);
